/**
 * Разделы фильтров, доступные при создании «Группы регистраций».
 * Группа привязана к сущности «Регистрации» — фильтр только по регистрациям.
 */
export const REGISTRATION_FILTER_SECTIONS: string[] = [ 'Регистрации' ];

/**
 * Числовой идентификатор раздела (section_id) для групп регистраций.
 * Справочник разделов: 1 = reg (Регистрации), 2 = orders (Заказы, появятся позже).
 * Задаётся контекстом дерева, пользователю в UI не показывается.
 */
export const REGISTRATION_SECTION_ID = 1;

/**
 * Данные наполнения связи «группа ↔ регистрация», приходят в data.membership
 * после синхронного наполнения на бэке при создании/редактировании группы.
 */
export interface GroupMembership {
  // Время наполнения связи в миллисекундах
  duration_ms?: number;
  // Сколько связей записано = сколько регистраций попало в группу
  inserted?: number;
  // true — наполнение не выполнялось (раздел пока не поддерживается, напр. заказы)
  skipped?: boolean;
}

/**
 * Форматирует время наполнения: <1000 мс → «158 мс», ≥1000 мс → «1.3 с».
 */
export const formatMembershipDuration = (durationMs: number): string => {
  const safeMs = Number.isFinite(durationMs) && durationMs > 0 ? durationMs : 0;
  if (safeMs < 1000) return `${Math.round(safeMs)} мс`;
  return `${(safeMs / 1000).toFixed(1)} с`;
};

/**
 * Форматирует количество с разделителями разрядов (102363 → «102 363»).
 */
export const formatInsertedCount = (count: number): string => {
  const safeCount = Number.isFinite(count) ? Math.round(count) : 0;
  return safeCount.toLocaleString('ru-RU');
};

/**
 * Строит сообщение о результате наполнения группы для инфо-окна.
 * mode — 'create' (создание) или 'edit' (редактирование).
 * kind — 'success' (тост успеха) или 'warning' (предупреждение, не ошибка).
 * Если membership отсутствует (старый бэк без наполнения) — обычное сообщение об успехе.
 */
export const buildMembershipMessage = (
  membership: GroupMembership | undefined | null,
  mode: 'create' | 'edit'
): { kind: 'success' | 'warning'; text: string } => {
  // Старый бэк не прислал membership — показываем обычное подтверждение
  if (!membership) {
    return {
      kind: 'success',
      text: mode === 'create' ? 'Группа успешно создана' : 'Группа успешно обновлена',
    };
  }

  // Наполнение пропущено (раздел пока не поддерживается, напр. заказы)
  if (membership.skipped) {
    return {
      kind: 'success',
      text: 'Группа сохранена. Наполнение для этого раздела пока не выполняется.',
    };
  }

  const inserted = Number(membership.inserted ?? 0);

  // Пустая группа — предупреждение, а не ошибка
  if (inserted === 0) {
    return {
      kind: 'warning',
      text: 'Под условие группы не попало ни одной регистрации. Проверьте фильтр.',
    };
  }

  const insertedText = formatInsertedCount(inserted);
  const durationText = formatMembershipDuration(Number(membership.duration_ms ?? 0));

  return {
    kind: 'success',
    text:
      mode === 'create'
        ? `Группа создана. Наполнено ${insertedText} регистраций за ${durationText}.`
        : `Группа обновлена. Связь пересоздана: ${insertedText} регистраций за ${durationText}.`,
  };
};
